import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    FormControlLabel,
    Switch,
    RadioGroup,
    Radio,
    useTheme,
    Stack,
    Button,
    Checkbox,
    CircularProgress,
    Snackbar,
    Alert,
    InputAdornment,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { languageService, Language } from '../../services/language-service';
import { tppService, ThirdPartyProcessorTranslation } from '../../services/tpp-service';
import { translationService } from '../../services/translation-service';
import { config } from '../../config/config';
import LanguagesImg from '../../assets/images/Languages.png';
import AIIcon from '../../assets/images/Artificial_intelligence.png';

const PROCESSOR_TYPES = [
    { value: 'PROCESSOR', label: 'Processor' },
    { value: 'FIDUCIARY', label: 'Fiduciary' }
];

interface EditProcessorPanelProps {
    open: boolean;
    onClose: () => void;
    processor: any | null;
    onSave?: () => void;
}

interface Translation {
    name: string;
    description: string;
}

interface InitialValues {
    name: string;
    description: string;
    type: string;
    websiteURL: string;
    contactEmail: string;
    contactPhone: string;
    dpaURL: string;
    translations: Record<string, Translation>;
}

const EditProcessorPanel: React.FC<EditProcessorPanelProps> = ({ open, onClose, processor, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [name, setName] = useState(processor?.name || '');
    const [description, setDescription] = useState(processor?.description || '');
    const [type, setType] = useState(processor?.type || 'PROCESSOR');
    const [websiteURL, setWebsiteURL] = useState(processor?.websiteURL || '');
    const [contactEmail, setContactEmail] = useState(processor?.contactEmail || '');
    const [contactPhone, setContactPhone] = useState(processor?.contactPhone || '');
    const [dpaURL, setDpaURL] = useState(processor?.dataProcessingAgreementURL || '');
    const [hasChanges, setHasChanges] = useState(false);
    const [initialValues, setInitialValues] = useState<InitialValues | undefined>();
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['en']);
    const [activeLanguage, setActiveLanguage] = useState<string>('en');
    const [translations, setTranslations] = useState<Record<string, Translation>>({});
    
    // Translation-specific states
    const [showStartTranslation, setShowStartTranslation] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [acceptedTranslations, setAcceptedTranslations] = useState<Set<string>>(new Set());
    const [originalTranslations, setOriginalTranslations] = useState<Record<string, string>>({});
    
    // Validation error states
    const [emailError, setEmailError] = useState('');
    const [websiteURLError, setWebsiteURLError] = useState('');
    const [dpaURLError, setDpaURLError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [nameError, setNameError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    const DRAWER_WIDTH = theme.customLayout.drawerWidth;
    const HEADER_HEIGHT = theme.customLayout.headerHeight;
    const MEDIUM_GAP = theme.customLayout.mediumGap;

    // Validation functions
    const validateEmail = (value: string): string => {
        if (!value?.trim()) return ''; // Email is optional
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    const validateURL = (value: string, fieldName: string): string => {
        if (!value?.trim()) return ''; // URLs are optional
        try {
            new URL(value);
            return '';
        } catch {
            return `Please enter a valid URL (e.g., https://example.com)`;
        }
    };

    const validatePhone = (value: string): string => {
        if (!value?.trim()) return ''; // Phone is optional
        // Remove any non-digit characters (including the +91 prefix)
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
            return 'Phone number must be 10 digits';
        }
        return '';
    };

    const validateName = (value: string): string => {
        if (!value?.trim()) {
            return 'Name is required';
        }
        if (value.trim().length < 3) {
            return 'Name must be at least 3 characters';
        }
        return '';
    };

    // Validation handlers
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setContactEmail(value);
        setEmailError(validateEmail(value));
    };

    const handleEmailBlur = () => {
        setEmailError(validateEmail(contactEmail));
    };

    const handleWebsiteURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setWebsiteURL(value);
        setWebsiteURLError(validateURL(value, 'Website URL'));
    };

    const handleWebsiteURLBlur = () => {
        setWebsiteURLError(validateURL(websiteURL, 'Website URL'));
    };

    const handleDpaURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDpaURL(value);
        setDpaURLError(validateURL(value, 'Data Processing Agreement URL'));
    };

    const handleDpaURLBlur = () => {
        setDpaURLError(validateURL(dpaURL, 'Data Processing Agreement URL'));
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Remove +91 prefix if user tries to type it, only allow digits
        const digitsOnly = value.replace(/\D/g, '');
        const formattedValue = digitsOnly.slice(0, 10); // Limit to 10 digits
        setContactPhone(formattedValue);
        setPhoneError(validatePhone(formattedValue));
    };

    const handlePhoneBlur = () => {
        setPhoneError(validatePhone(contactPhone));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        setNameError(validateName(value));
    };

    const handleNameBlur = () => {
        setNameError(validateName(name));
    };

    const checkForChanges = (): boolean => {
        if (!initialValues) return false;
        
        if (name !== initialValues.name ||
            description !== initialValues.description ||
            type !== initialValues.type ||
            websiteURL !== initialValues.websiteURL ||
            contactEmail !== initialValues.contactEmail ||
            contactPhone !== initialValues.contactPhone ||
            dpaURL !== initialValues.dpaURL) {
            return true;
        }

        // Check for translation changes
        const currentTranslation = translations[activeLanguage];
        if (currentTranslation) {
            const originalTranslation = initialValues.translations[activeLanguage];
            if (originalTranslation) {
                if (currentTranslation.name !== originalTranslation.name ||
                    currentTranslation.description !== originalTranslation.description) {
                    return true;
                }
            }
        }

        return false;
    };

    const hasValidationErrors = (): boolean => {
        return !!(emailError || websiteURLError || dpaURLError || phoneError || nameError);
    };

    // Translation change detection
    const hasTranslationChanges = () => {
        const currentText = translations[activeLanguage]?.name || '';
        const originalText = originalTranslations[activeLanguage] || '';
        return currentText !== originalText;
    };

    const hasAnyTranslationChanges = () => {
        return Object.keys(translations).some(langCode => {
            if (langCode === 'en') return false; // Skip English as it's the source
            const currentText = translations[langCode]?.name || '';
            const originalText = originalTranslations[langCode] || '';
            return currentText !== originalText;
        });
    };

    React.useEffect(() => {
        if (!open) {
            // Reset states when panel closes
            setCurrentTab(0);
            setActiveLanguage('en');
            setSelectedLanguages(['en']);
            setEmailError('');
            setWebsiteURLError('');
            setDpaURLError('');
            setPhoneError('');
            setNameError('');
            setShowStartTranslation(false);
            setAcceptedTranslations(new Set());
            setOriginalTranslations({});
            setIsPublishing(false);
            return;
        }
        
        setCurrentTab(0);
        if (processor) {
            setName(processor.name || '');
            setDescription(processor.description || '');
            setType(processor.type || 'PROCESSOR');
            setWebsiteURL(processor.websiteURL || '');
            setContactEmail(processor.contactEmail || '');
            // Remove +91 prefix from phone number for display
            const phoneWithoutPrefix = processor.contactPhone?.replace(/^\+91/, '') || '';
            setContactPhone(phoneWithoutPrefix);
            setDpaURL(processor.dataProcessingAgreementURL || '');
            
            // Initialize translations
            const newTranslations = {
                en: {
                    name: processor.name || '',
                    description: processor.description || '',
                }
            };
            setTranslations(newTranslations);
            
            setInitialValues({
                name: processor.name || '',
                description: processor.description || '',
                type: processor.type || 'PROCESSOR',
                websiteURL: processor.websiteURL || '',
                contactEmail: processor.contactEmail || '',
                contactPhone: phoneWithoutPrefix,
                dpaURL: processor.dataProcessingAgreementURL || '',
                translations: newTranslations
            });

            // Fetch full processor data including translations
            tppService.getProcessorById(processor.processorId)
                .then(fullProcessor => {
                    if (!fullProcessor) {
                        setShowStartTranslation(true);
                        return;
                    }
                    
                    if (!fullProcessor.translations || fullProcessor.translations.length === 0) {
                        setShowStartTranslation(true);
                        return;
                    }
                    
                    setShowStartTranslation(false);
                    const translationsMap: Record<string, Translation> = {
                        en: {
                            name: fullProcessor.name || '',
                            description: fullProcessor.description || '',
                        }
                    };
                    
                    const accepted = new Set<string>();
                    const original: Record<string, string> = {};
                    
                    // Map server translations to our local format
                    fullProcessor.translations.forEach((translation: ThirdPartyProcessorTranslation) => {
                        if (translation.languageCode !== 'en') {
                            translationsMap[translation.languageCode] = {
                                name: translation.translatedName,
                                description: translation.translatedDescription || '',
                            };
                            accepted.add(translation.languageCode);
                            original[translation.languageCode] = translation.translatedName;
                        }
                    });
                    
                    setTranslations(translationsMap);
                    setAcceptedTranslations(accepted);
                    setOriginalTranslations(original);
                    setInitialValues(prev => prev ? {
                        ...prev,
                        translations: translationsMap
                    } : undefined);
                })
                .catch((error: any) => {
                    console.error('Failed to fetch processor translations:', error);
                    setError('Failed to load translations');
                });
        }
        
        // Fetch supported languages
        languageService.getSupportedLanguages()
            .then(languages => {
                setLanguages(languages);
                // Set the first non-English language as active if available
                const firstNonEnglishLang = languages.find(lang => lang.code !== 'en');
                if (firstNonEnglishLang) {
                    setActiveLanguage(firstNonEnglishLang.code);
                    setSelectedLanguages([firstNonEnglishLang.code]);
                }
            })
            .catch((error: any) => {
                console.error('Failed to fetch supported languages:', error);
                setError('Failed to load supported languages');
            });
            
        setHasChanges(false);
    }, [processor, open]);

    // Add effect to track changes
    useEffect(() => {
        setHasChanges(checkForChanges());
    }, [name, description, type, websiteURL, contactEmail, contactPhone, dpaURL, translations, activeLanguage, initialValues]);

    // Handlers for translations
    const handleLanguageToggle = (code: string) => {
        setSelectedLanguages((prev) =>
            prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
        );
        if (!translations[code]) {
            setTranslations((prev) => ({ ...prev, [code]: { name: '', description: '' } }));
        }
    };

    const handleTranslationChange = (field: 'name' | 'description', value: string) => {
        setTranslations((prev) => ({
            ...prev,
            [activeLanguage]: {
                ...prev[activeLanguage],
                [field]: value,
            },
        }));
    };

    // AI Translation handler
    const handleTranslateAI = async () => {
        if (!translations['en']?.name) {
            setError('Please ensure English name is available for translation');
            return;
        }

        setIsTranslating(true);
        setError(null);

        try {
            const translatedText = await translationService.translate(
                translations['en'].name,
                activeLanguage
            );

            setTranslations(prev => ({
                ...prev,
                [activeLanguage]: {
                    ...prev[activeLanguage],
                    name: translatedText
                }
            }));
        } catch (error: any) {
            console.error('Translation error:', error);
            setError(error.response?.data?.message || 'Failed to translate text');
        } finally {
            setIsTranslating(false);
        }
    };

    // Accept translation handler
    const handleAcceptTranslation = async () => {
        if (!processor?.processorId || !translations[activeLanguage]?.name) {
            setError('Please ensure translation text is available');
            return;
        }

        setIsSavingDetails(true);
        setError(null);

        try {
            await tppService.updateTranslation(
                processor.processorId,
                {
                    processorId: processor.processorId,
                    languageCode: activeLanguage,
                    translatedName: translations[activeLanguage].name,
                    translatedDescription: translations[activeLanguage].description || '',
                    isActive: true
                }
            );

            // Add to accepted translations and update original
            setAcceptedTranslations(prev => new Set(Array.from(prev).concat(activeLanguage)));
            setOriginalTranslations(prev => ({
                ...prev,
                [activeLanguage]: translations[activeLanguage].name
            }));

            // Refresh translations list by fetching the full processor
            const updatedProcessor = await tppService.getProcessorById(processor.processorId);
            if (updatedProcessor) {
                const translationsMap: Record<string, Translation> = {
                    en: {
                        name: updatedProcessor.name || '',
                        description: updatedProcessor.description || '',
                    }
                };
                
                const accepted = new Set<string>();
                const original: Record<string, string> = {};
                if (updatedProcessor.translations) {
                    updatedProcessor.translations.forEach((translation: ThirdPartyProcessorTranslation) => {
                        if (translation.languageCode !== 'en') {
                            translationsMap[translation.languageCode] = {
                                name: translation.translatedName,
                                description: translation.translatedDescription || '',
                            };
                            accepted.add(translation.languageCode);
                            original[translation.languageCode] = translation.translatedName;
                        }
                    });
                }
                
                setTranslations(translationsMap);
                setAcceptedTranslations(accepted);
                setOriginalTranslations(original);
            }
        } catch (error: any) {
            console.error('Error saving translation:', error);
            setError(error.response?.data?.message || 'Failed to save translation');
        } finally {
            setIsSavingDetails(false);
        }
    };

    const handleSubmit = async () => {
        if (!processor) return;
        
        // Validate all fields before submitting
        const nameValidation = validateName(name);
        const emailValidation = validateEmail(contactEmail);
        const websiteValidation = validateURL(websiteURL, 'Website URL');
        const dpaValidation = validateURL(dpaURL, 'Data Processing Agreement URL');
        const phoneValidation = validatePhone(contactPhone);
        
        setNameError(nameValidation);
        setEmailError(emailValidation);
        setWebsiteURLError(websiteValidation);
        setDpaURLError(dpaValidation);
        setPhoneError(phoneValidation);
        
        if (nameValidation || emailValidation || websiteValidation || dpaValidation || phoneValidation) {
            setError('Please fix validation errors before saving');
            return;
        }
        
        setIsSavingDetails(true);
        setError(null);
        
        try {
            // Create the processor update object
            const processorUpdate = {
                name,
                description,
                type,
                website: websiteURL,
                contactEmail,
                contactPhone: contactPhone ? `+91${contactPhone}` : '', // Add +91 prefix when saving
                dataProcessingAgreementUrl: dpaURL
            };

            await tppService.updateProcessor(processor.processorId, processorUpdate);
            onSave?.();
            onClose();
        } catch (error: any) {
            console.error('Error updating processor:', error);
            setError(error.response?.data?.message || 'Failed to update processor');
        } finally {
            setIsSavingDetails(false);
        }
    };

    const handleClose = () => {
        // Check for unsaved changes in both tabs
        const hasDetailsChanges = hasChanges;
        const hasTranslationChanges = hasAnyTranslationChanges();

        if (hasDetailsChanges || hasTranslationChanges) {
            const confirmMessage = `You have unsaved changes.\n\n` +
                `• Details tab: ${hasDetailsChanges ? 'Modified' : 'No changes'}\n` +
                `• Translations tab: ${hasTranslationChanges ? 'Modified' : 'No changes'}\n\n` +
                `Are you sure you want to close? All unsaved changes will be lost.`;

            if (!window.confirm(confirmMessage)) {
                return; // User cancelled, don't close
            }
        }

        onClose();
    };

    const handlePublishToggle = async () => {
        if (!processor) return;

        const isCurrentlyPublished = processor.isPublished;

        if (isCurrentlyPublished) {
            // If already published, show confirmation for unpublishing
            const confirmUnpublish = window.confirm(
                'Are you sure you want to unpublish this processor?\n\n' +
                'Unpublishing will make this processor unavailable to other parts of the program.\n' +
                'You can publish it again later when needed.'
            );

            if (!confirmUnpublish) {
                return;
            }

            try {
                setIsPublishing(true);
                await tppService.unpublishProcessor(processor.processorId);
                onSave?.();
                onClose(); // Close the modal after successful unpublish
            } catch (error: any) {
                setError(error.response?.data?.message || 'Failed to unpublish processor');
            } finally {
                setIsPublishing(false);
            }
            return;
        }

        // Step 1: Check for unsaved changes in both tabs
        const hasDetailsChanges = hasChanges;
        const hasTranslationChanges = hasAnyTranslationChanges();

        if (hasDetailsChanges || hasTranslationChanges) {
            setError('Please save all changes before publishing the processor');
            return;
        }

        // Step 2: Validate Details tab
        // Validate name
        if (!name || name.length < 3) {
            setError('Processor name must be at least 3 characters long');
            return;
        }

        // Validate description if present
        if (description && description.length < 3) {
            setError('Description must be at least 3 characters long if provided');
            return;
        }

        // Validate type
        if (!type) {
            setError('Processor type must be selected');
            return;
        }

        // Step 3: Validate Translations tab
        const supportedLanguages = languages.filter(lang => lang.translationProvider !== null);
        const missingTranslations = supportedLanguages.filter(lang => 
            lang.code !== 'en' && (!translations[lang.code]?.name || 
            translations[lang.code].name.length < 3)
        );

        const unacceptedTranslations = supportedLanguages.filter(lang => 
            lang.code !== 'en' && translations[lang.code]?.name && 
            !acceptedTranslations.has(lang.code)
        );

        // Build comprehensive confirmation message
        let confirmMessage = `Are you sure you want to publish this processor?\n\n` +
            `Publishing will make this processor available to other parts of the program to use.\n\n` +
            `Please ensure that you have:\n` +
            `• Reviewed all translations\n` +
            `• Verified the processor name and description\n` +
            `• Selected the correct processor type\n\n`;

        // Add translation warnings if any
        if (missingTranslations.length > 0 || unacceptedTranslations.length > 0) {
            confirmMessage += `⚠️  WARNING:\n`;
            
            if (missingTranslations.length > 0) {
                confirmMessage += `• ${missingTranslations.length} language(s) do not have translations\n`;
            }
            
            if (unacceptedTranslations.length > 0) {
                confirmMessage += `• ${unacceptedTranslations.length} language(s) have unaccepted translations\n`;
            }
            
            confirmMessage += `\nYou can still publish, but these translations will not be available.\n\n`;
        }

        confirmMessage += `This action cannot be undone.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        // If user confirms, publish the processor
        try {
            setIsPublishing(true);
            await tppService.publishProcessor(processor.processorId);
            onSave?.();
            onClose(); // Close the modal after successful publish
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to publish processor');
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-processor-modal-title"
            sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', zIndex: 1301 }}
        >
            <React.Fragment>
                <Box
                    sx={{
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        boxShadow: theme.shadows[24],
                        p: theme.spacing(0),
                        width: `calc(100vw - ${DRAWER_WIDTH + 2 * MEDIUM_GAP}px)`,
                        height: `calc(100vh - ${HEADER_HEIGHT + 2 * MEDIUM_GAP}px)`,
                        position: 'absolute',
                        left: `${DRAWER_WIDTH + MEDIUM_GAP}px`,
                        top: `${HEADER_HEIGHT + MEDIUM_GAP}px`,
                        right: `${MEDIUM_GAP}px`,
                        bottom: `${MEDIUM_GAP}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                    }}
                >
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}>
                        <Typography id="edit-processor-modal-title" variant="h6" sx={{ color: theme.palette.text.primary }}>
                            Edit Processor
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePublishToggle}
                                disabled={isPublishing || isSavingDetails || isDeleting}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        bgcolor: theme.palette.primary.dark,
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: theme.palette.action.disabledBackground,
                                        color: theme.palette.action.disabled,
                                    }
                                }}
                            >
                                {isPublishing ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    processor?.isPublished ? 'Unpublish Processor' : 'Publish Processor'
                                )}
                            </Button>
                            <IconButton 
                                onClick={handleClose} 
                                disabled={isPublishing || isSavingDetails || isDeleting}
                                sx={{ color: theme.palette.text.primary }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    {/* Tabs */}
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Tabs
                            value={currentTab}
                            onChange={(_, v) => setCurrentTab(v)}
                            sx={{
                                '& .MuiTab-root': {
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Details
                                        {hasChanges && currentTab === 0 && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                } 
                            />
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Translations
                                        {currentTab === 1 && hasAnyTranslationChanges() && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                } 
                            />
                        </Tabs>
                    </Box>
                    {/* Tab Panels */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {currentTab === 0 && (
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <TextField 
                                        label="Name" 
                                        fullWidth 
                                        size="small" 
                                        required 
                                        value={name} 
                                        onChange={handleNameChange}
                                        onBlur={handleNameBlur}
                                        error={!!nameError}
                                        helperText={nameError}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: theme.palette.background.paper,
                                                '& fieldset': {
                                                    borderColor: nameError ? theme.palette.error.main : theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                        disabled={processor?.isPublished}
                                    />
                                    <TextField 
                                        label="Description" 
                                        fullWidth 
                                        size="small" 
                                        multiline 
                                        rows={3} 
                                        value={description} 
                                        onChange={e => setDescription(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: theme.palette.background.paper,
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                        disabled={processor?.isPublished}
                                    />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>Type</Typography>
                                        <RadioGroup row value={type} onChange={e => setType(e.target.value)}>
                                            {PROCESSOR_TYPES.map(opt => (
                                                <FormControlLabel 
                                                    key={opt.value} 
                                                    value={opt.value} 
                                                    control={
                                                        <Radio 
                                                            disabled={processor?.isPublished}
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                '&.Mui-checked': {
                                                                    color: theme.palette.primary.main,
                                                                },
                                                            }}
                                                        />
                                                    } 
                                                    label={opt.label}
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                    }}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField 
                                            label="Contact Email ID" 
                                            fullWidth 
                                            size="small" 
                                            value={contactEmail} 
                                            onChange={handleEmailChange}
                                            onBlur={handleEmailBlur}
                                            error={!!emailError}
                                            helperText={emailError}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: theme.palette.background.paper,
                                                    '& fieldset': {
                                                        borderColor: emailError ? theme.palette.error.main : theme.palette.divider,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                },
                                            }}
                                            disabled={processor?.isPublished}
                                        />
                                        <TextField 
                                            label="Contact Phone Number" 
                                            fullWidth 
                                            size="small" 
                                            value={contactPhone} 
                                            onChange={handlePhoneChange}
                                            onBlur={handlePhoneBlur}
                                            error={!!phoneError}
                                            helperText={phoneError || "Enter 10-digit phone number"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        +91
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: theme.palette.background.paper,
                                                    '& fieldset': {
                                                        borderColor: phoneError ? theme.palette.error.main : theme.palette.divider,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                },
                                            }}
                                            disabled={processor?.isPublished}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField 
                                            label="Website URL" 
                                            fullWidth 
                                            size="small" 
                                            value={websiteURL} 
                                            onChange={handleWebsiteURLChange}
                                            onBlur={handleWebsiteURLBlur}
                                            error={!!websiteURLError}
                                            helperText={websiteURLError || "Enter a valid URL (e.g., https://example.com)"}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: theme.palette.background.paper,
                                                    '& fieldset': {
                                                        borderColor: websiteURLError ? theme.palette.error.main : theme.palette.divider,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                },
                                            }}
                                            disabled={processor?.isPublished}
                                        />
                                        <TextField 
                                            label="Data Processing Agreement URL" 
                                            fullWidth 
                                            size="small" 
                                            value={dpaURL} 
                                            onChange={handleDpaURLChange}
                                            onBlur={handleDpaURLBlur}
                                            error={!!dpaURLError}
                                            helperText={dpaURLError || "Enter a valid URL (e.g., https://example.com/dpa)"}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: theme.palette.background.paper,
                                                    '& fieldset': {
                                                        borderColor: dpaURLError ? theme.palette.error.main : theme.palette.divider,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                },
                                            }}
                                            disabled={processor?.isPublished}
                                        />
                                    </Box>
                                    {!processor?.isPublished && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={!hasChanges || isSavingDetails || hasValidationErrors()}
                                                onClick={handleSubmit}
                                                sx={{
                                                    bgcolor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.primary.dark,
                                                    },
                                                    '&.Mui-disabled': {
                                                        bgcolor: theme.palette.action.disabledBackground,
                                                        color: theme.palette.action.disabled,
                                                    },
                                                    minWidth: 100,
                                                }}
                                            >
                                                {isSavingDetails ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Save'
                                                )}
                                            </Button>
                                        </Box>
                                    )}

                                    {!processor?.isPublished && (
                                        <Box sx={{ 
                                            mt: 4, 
                                            p: 3, 
                                            border: `1px solid ${theme.palette.error.main}`,
                                            borderRadius: theme.shape.borderRadius,
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.08)' : 'rgba(211, 47, 47, 0.04)',
                                        }}>
                                            <Typography variant="h6" sx={{ color: theme.palette.error.main, mb: 1 }}>
                                                Danger Zone
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 2 }}>
                                                Processor once deleted cannot be retrieved
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={async () => {
                                                    if (window.confirm('Are you sure you want to delete this processor? This action cannot be undone.')) {
                                                        if (processor?.processorId) {
                                                            try {
                                                                setIsDeleting(true);
                                                                await tppService.deleteProcessor(processor.processorId);
                                                                onSave?.();
                                                                onClose();
                                                            } catch (error: any) {
                                                                // Check if it's a permission error
                                                                if (error.response?.status === 403) {
                                                                    setError('You do not have permission to delete this processor');
                                                                } else if (error.response?.status === 404) {
                                                                    setError('Processor not found');
                                                                } else {
                                                                    setError(error.response?.data?.message || 'Failed to delete processor');
                                                                }
                                                            } finally {
                                                                setIsDeleting(false);
                                                            }
                                                        }
                                                    }
                                                }}
                                                disabled={isDeleting || isSavingDetails}
                                                sx={{
                                                    bgcolor: theme.palette.error.main,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.error.dark,
                                                    },
                                                }}
                                            >
                                                {isDeleting ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Delete Processor'
                                                )}
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        
                        {currentTab === 1 && showStartTranslation ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                textAlign: 'center',
                            }}>
                                <img src={LanguagesImg} alt="Languages" style={{ width: 80, height: 80, marginBottom: 24 }} />
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Use this space to translate the Processor in 22 languages as required by the DPDP Act.<br />
                                    Please ensure that you have finalized the text in the default language before proceeding.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="success"
                                    sx={{ mb: 2, px: 4 }}
                                    onClick={() => {
                                        setShowStartTranslation(false);
                                        if (languages.length > 0) {
                                            setActiveLanguage(languages[0].code);
                                            setSelectedLanguages([languages[0].code]);
                                        }
                                    }}
                                >
                                    START TRANSLATION
                                </Button>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    PS. We provide translation using AI :)
                                </Typography>
                            </Box>
                        ) : null}

                        {currentTab === 1 && !showStartTranslation && (
                            <Box sx={{ p: 3, display: 'flex', gap: 3, height: `calc(100% - ${MEDIUM_GAP}px)` }}>
                                {/* Table-like language list */}
                                <Box sx={{ 
                                    minWidth: 380, 
                                    height: '100%', 
                                    maxHeight: '100%', 
                                    overflowY: 'auto', 
                                    border: `1px solid ${theme.palette.divider}`, 
                                    borderRadius: theme.shape.borderRadius, 
                                    p: 0, 
                                    bgcolor: theme.palette.background.paper, 
                                    mb: `${MEDIUM_GAP}px`, 
                                    display: 'flex', 
                                    flexDirection: 'column' 
                                }}>
                                    {/* Header Row */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        px: 2, 
                                        py: 1, 
                                        borderBottom: `1px solid ${theme.palette.divider}`, 
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50], 
                                        fontWeight: 600 
                                    }}>
                                        <Typography variant="body2" sx={{ flex: 2, fontWeight: 600, color: theme.palette.text.primary }}>Languages</Typography>
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: theme.palette.text.primary }}>Code</Typography>
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.primary }}>Accepted</Typography>
                                    </Box>
                                    {/* Language Rows */}
                                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                        {languages.map((lang) => {
                                            const isSelected = activeLanguage === lang.code;
                                            const isNotSupported = lang.translationProvider === null;
                                            const rowContent = (
                                                <Box
                                                    key={lang.code}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        py: 1,
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: isSelected ? theme.palette.action.selected : 'inherit',
                                                        opacity: isNotSupported ? 0.5 : 1,
                                                        transition: 'background 0.2s',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: isNotSupported ? undefined : theme.palette.action.hover,
                                                        },
                                                    }}
                                                    onClick={() => setActiveLanguage(lang.code)}
                                                >
                                                    <Box sx={{ flex: 2 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline', color: theme.palette.text.primary }}>{lang.name}</Typography>
                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, display: 'block', ml: 0.5 }}>
                                                            {lang.localeName}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{lang.code.toUpperCase()}</Typography>
                                                        {lang.code !== 'en' && translations[lang.code]?.name !== originalTranslations[lang.code] && (
                                                            <Typography 
                                                                component="span" 
                                                                sx={{ 
                                                                    color: theme.palette.primary.main,
                                                                    fontSize: '0.875rem',
                                                                    lineHeight: 1,
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                •
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                                        <Checkbox
                                                            checked={acceptedTranslations.has(lang.code)}
                                                            readOnly
                                                            size="small"
                                                            sx={{ p: 0 }}
                                                        />
                                                    </Box>
                                                </Box>
                                            );
                                            return isNotSupported ? (
                                                <Tooltip key={lang.code} title="This language is not supported by AI" arrow>
                                                    <span style={{ display: 'block' }}>{rowContent}</span>
                                                </Tooltip>
                                            ) : rowContent;
                                        })}
                                    </Box>
                                </Box>

                                {/* Translation form for selected language */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                                            English Reference
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                            {translations['en']?.name || ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                            Language: {languages.find((l) => l.code === activeLanguage)?.name || activeLanguage}
                                        </Typography>
                                        {!processor?.isPublished && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={isTranslating ? <CircularProgress size={20} /> : <img src={AIIcon} alt="AI" style={{ width: 20, height: 20 }} />}
                                                sx={{
                                                    ml: 2,
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    borderColor: theme.palette.primary.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.dark,
                                                        color: theme.palette.primary.dark,
                                                    },
                                                }}
                                                disabled={languages.find((l) => l.code === activeLanguage)?.translationProvider === null || isTranslating}
                                                onClick={handleTranslateAI}
                                            >
                                                {isTranslating ? 'TRANSLATING...' : 'TRANSLATE USING AI'}
                                            </Button>
                                        )}
                                        <Tooltip
                                            title="AI-generated translations may contain errors or inaccuracies. Please review and cross-check the translation before accepting."
                                            arrow
                                        >
                                            <InfoOutlinedIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    ml: 1, 
                                                    color: theme.palette.neutral ? theme.palette.neutral.main : theme.palette.text.secondary, 
                                                    cursor: 'pointer',
                                                    fontWeight: 400
                                                }} 
                                            />
                                        </Tooltip>
                                    </Box>
                                    <TextField
                                        label="Name"
                                        fullWidth
                                        size="small"
                                        required
                                        value={translations[activeLanguage]?.name || ''}
                                        onChange={e => handleTranslationChange('name', e.target.value)}
                                        disabled={processor?.isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                    />
                                    <TextField
                                        label="Description"
                                        fullWidth
                                        size="small"
                                        multiline
                                        rows={3}
                                        value={translations[activeLanguage]?.description || ''}
                                        onChange={e => handleTranslationChange('description', e.target.value)}
                                        disabled={processor?.isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                    />
                                    {!processor?.isPublished && (
                                        <Button 
                                            variant="contained" 
                                            sx={{ 
                                                alignSelf: 'flex-start', 
                                                mt: 2,
                                                bgcolor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                '&:hover': {
                                                    bgcolor: theme.palette.primary.dark,
                                                },
                                            }}
                                            onClick={handleAcceptTranslation}
                                            disabled={isSavingDetails || !translations[activeLanguage]?.name || !hasTranslationChanges()}
                                        >
                                            {isSavingDetails ? <CircularProgress size={24} color="inherit" /> : 'Accept'}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Error Snackbar */}
                <Snackbar 
                    open={!!error} 
                    autoHideDuration={config.ui.snackbar.autoHideDuration}
                    onClose={() => setError(null)}
                    anchorOrigin={config.ui.snackbar.anchorOrigin}
                >
                    <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>
            </React.Fragment>
        </Modal>
    );
};

export default EditProcessorPanel; 